import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { useScrollReveal } from '../hooks/useScrollReveal';

const TEAM = [
  {
    name: 'P R',
    role: 'Senior Web Developer',
    bio: 'Senior Web Developer with 8+ years of experience in Shopify, WordPress, Webflow, and React. Previously worked as a Team Lead, successfully leading development teams and delivering modern, scalable, and high-performing web solutions.',
    initials: 'PR'
  },

  {
    name: 'M R',
    role: 'Senior Hybrid App Developer',
    bio: 'Hybrid App Developer with 9+ years of experience in building high-performance mobile applications for both iOS and Android. Experienced in delivering scalable, user-focused solutions and contributing to successful product launches across multiple industries.',
    initials: 'MR'
  },

  {
    name: 'S S',
    role: 'Senior Web Developer',
    bio: 'Senior Web Developer with extensive experience in Shopify, WordPress, Webflow, and React. Skilled in building modern, responsive, and high-performing websites, with a strong focus on clean development, scalability, and seamless user experiences.',
    initials: 'SS'
  },
];

const VALUES = [
  {
    icon: (
      <svg
        width="28"
        height="28"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </svg>
    ),
    title: 'Results First',
    desc: 'We measure success by your growth — traffic, leads, revenue. Not vanity metrics.'
  },

  {
    icon: (
      <svg
        width="28"
        height="28"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M21 11.5a8.4 8.4 0 0 1-9 8.5 8.7 8.7 0 0 1-4-.9L3 21l1.9-4.2A8.4 8.4 0 0 1 3 11.5 9 9 0 0 1 12 3a9 9 0 0 1 9 8.5Z" />
        <path d="M8 10h8" />
        <path d="M8 14h5" />
      </svg>
    ),
    title: 'Honest Communication',
    desc: 'We tell you what you need to hear, not what you want to hear. No overpromising.'
  },

  {
    icon: (
      <svg
        width="28"
        height="28"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="m13 2-9 12h7l-1 8 9-12h-7l1-8Z" />
      </svg>
    ),
    title: 'Speed & Quality',
    desc: 'We move fast without cutting corners. Quick delivery with no compromise on quality.'
  },

  {
    icon: (
      <svg
        width="28"
        height="28"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M20 11a8 8 0 0 0-14.9-4" />
        <path d="M4 5v5h5" />
        <path d="M4 13a8 8 0 0 0 14.9 4" />
        <path d="M20 19v-5h-5" />
      </svg>
    ),
    title: 'Long-Term Thinking',
    desc: 'We build things that last and scale. Not quick fixes that break in 6 months.'
  },
];

const STATS = [
  { value: 500, suffix: '+', label: 'Projects Completed' },
  { value: 100, suffix: '+', label: 'Happy Clients' },
  { value: 8, suffix: '+', label: 'Years in Business' },
  { value: 10, suffix: '+', label: 'Team Members' },
];

const PROJECTS = [
  {
    url: require('../images/pf1.webp'),
    alt: 'Web Development Project'
  },
  {
    url: require('../images/pf2.webp'),
    alt: 'Education Website Project'
  },
  {
    url: require('../images/pf3.3.png'),
    label: 'Mind Sharper',
    tag: 'Android App'
  },
  {
    url: require('../images/pf4.webp'),
    alt: 'WordPress Development Project'
  },
  {
    url: require('../images/pf5.webp'),
    alt: 'OpenCart Development Project'
  },
  {
    url: require('../images/pf6.webp'),
    alt: 'WordPress Development Project'
  },
];

function AnimatedCounter({ value, suffix }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;

          const steps = 60;
          const increment = value / steps;
          let current = 0;

          const timer = setInterval(() => {
            current += increment;

            if (current >= value) {
              setCount(value);
              clearInterval(timer);
            } else {
              setCount(Math.floor(current));
            }
          }, 1800 / steps);
        }
      },
      { threshold: 0.5 }
    );

    if (ref.current) observer.observe(ref.current);

    return () => observer.disconnect();
  }, [value]);

  return (
    <span ref={ref}>
      {count}
      {suffix}
    </span>
  );
}

export default function About() {
  useScrollReveal();

  const [lightbox, setLightbox] = useState({
    open: false,
    index: 0
  });

  const prev = () =>
    setLightbox(l => ({
      ...l,
      index: (l.index - 1 + PROJECTS.length) % PROJECTS.length
    }));

  const next = () =>
    setLightbox(l => ({
      ...l,
      index: (l.index + 1) % PROJECTS.length
    }));

  const close = () =>
    setLightbox({
      open: false,
      index: 0
    });

  return (
    <>
      <SEO
        title="About Us — IT Services Company"
        description="CodeViora is an IT services company specializing in web development, web design, app development, SEO, and digital marketing. Meet the team behind 100+ successful projects."
        canonical="/about"
      />

      {/* Hero */}
      <section className="bg-grid-black pt-16 pb-16 md:pt-20 md:pb-20 relative overflow-hidden">
        <div className="container-max relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">

            <div className="animate-fade-up">
              <span className="section-label-inv">
                About CodeViora
              </span>

              <h1 className="section-title-inv text-5xl md:text-6xl mt-2">
                WE BUILD THE INTERNET FOR BUSINESSES.
              </h1>

              <p className="section-sub-inv text-lg">
                CodeViora is an IT services company that helps businesses
                establish, grow, and dominate their digital presence.
              </p>

              <div className="flex flex-wrap gap-4 mt-8">
                <Link to="/services" className="btn-primary-inv">
                  Our Services
                </Link>

                <Link to="/contact" className="btn-outline-inv">
                  Work With Us
                </Link>
              </div>
            </div>

            <div
              className="hidden lg:block animate-fade-in"
              style={{ animationDelay: '0.2s' }}
            >
              <img
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80"
                alt="CodeViora team collaborating"
                className="rounded-3xl img-reveal w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                loading="lazy"
              />
            </div>

          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-pure-black border-y border-white/10 py-10 rounded-b-[50px] md:rounded-b-[100px]">
        <div className="container-max">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">

            {STATS.map(({ value, suffix, label }) => (
              <div key={label} className="reveal">

                <p
                  className="text-4xl text-white"
                  style={{
                    fontFamily: 'Anton,sans-serif',
                    fontWeight: 500
                  }}
                >
                  <AnimatedCounter
                    value={value}
                    suffix={suffix}
                  />
                </p>

                <p className="text-white/40 text-xs font-medium mt-1">
                  {label}
                </p>

              </div>
            ))}

          </div>
        </div>
      </section>

      {/* Story */}
      <section className="bg-grid-white py-20 md:py-28">
        <div className="container-max">

          <div className="grid lg:grid-cols-2 gap-16 items-center">

            <div className="reveal">
              <img
                src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&q=80"
                alt="CodeViora office workspace"
                className="rounded-3xl img-reveal w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                loading="lazy"
              />
            </div>

            <div
              className="reveal"
              style={{ transitionDelay: '100ms' }}
            >
              <span className="section-label">
                Our Story
              </span>

              <h2 className="section-title text-3xl md:text-4xl mt-1">
                FROM A SMALL TEAM TO A FULL IT STUDIO.
              </h2>

              <div className="flex flex-col gap-4 mt-6 text-black/60 dark:text-white/60 text-sm leading-relaxed">

                <p>
                  CodeViora started with a simple belief: every business
                  deserves a strong digital presence, but most agencies make
                  it too complicated, too expensive, or too slow. We set out
                  to change that.
                </p>

                <p>
                  We started as a two-person web development team. Today, we
                  are a full-service IT company offering web development,
                  app development, design, SEO, and digital marketing —
                  with 100+ completed projects and 25+ satisfied clients.
                </p>

                <p>
                  Our clients range from local businesses just getting online
                  to funded startups scaling fast. What stays constant is our
                  commitment: deliver real results, on time, with complete
                  transparency.
                </p>

              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="bg-grid-black py-20 md:py-28">
        <div className="container-max">

          <div className="reveal text-center mb-14">

            <span className="section-label-inv">
              What We Do
            </span>

            <h2 className="section-title-inv">
              END-TO-END IT SERVICES.
            </h2>

            <p className="section-sub-inv mx-auto">
              Everything your business needs to succeed online, delivered
              by one dedicated team.
            </p>

          </div>

          {(() => {

            const items = [

              {
                icon: (
                  <svg
                    width="28"
                    height="28"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <path d="M2 12h20" />
                    <path d="M12 2a15 15 0 0 1 0 20" />
                    <path d="M12 2a15 15 0 0 0 0 20" />
                  </svg>
                ),
                title: 'Web Development',
                desc: 'Custom websites, web apps, WordPress, Shopify — built fast and built to last.'
              },

              {
                icon: (
                  <svg
                    width="28"
                    height="28"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 20h9" />
                    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1-1 4 4-1 12.5-12.5a2.1 2.1 0 0 0-3-3Z" />
                  </svg>
                ),
                title: 'Web Design',
                desc: 'UI/UX design that converts visitors into customers. Premium, purposeful design.'
              },

              {
                icon: (
                  <svg
                    width="28"
                    height="28"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect
                      x="5"
                      y="2"
                      width="14"
                      height="20"
                      rx="2"
                    />
                    <path d="M9 18h6" />
                  </svg>
                ),
                title: 'App Development',
                desc: 'Cross-platform mobile apps for iOS and Android using React Native and Flutter.'
              },

              {
                icon: (
                  <svg
                    width="28"
                    height="28"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="11" cy="11" r="7" />
                    <path d="m20 20-4-4" />
                    <path d="M8 11h6" />
                    <path d="M11 8v6" />
                  </svg>
                ),
                title: 'SEO',
                desc: 'Data-driven SEO strategies that improve rankings and drive consistent organic traffic.'
              },

              {
                icon: (
                  <svg
                    width="28"
                    height="28"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="m22 2-7 20-4-9-9-4 20-7Z" />
                    <path d="M22 2 11 13" />
                  </svg>
                ),
                title: 'Digital Marketing',
                desc: 'Google Ads, Meta Ads, social media, and email marketing that delivers real ROI.'
              },

              {
                icon: (
                  <svg
                    width="28"
                    height="28"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 2v4" />
                    <path d="M12 18v4" />
                    <path d="m4.93 4.93 2.83 2.83" />
                    <path d="m16.24 16.24 2.83 2.83" />
                    <path d="M2 12h4" />
                    <path d="M18 12h4" />
                    <path d="m4.93 19.07 2.83-2.83" />
                    <path d="m16.24 7.76 2.83-2.83" />
                    <circle cx="12" cy="12" r="4" />
                  </svg>
                ),
                title: 'Maintenance & Support',
                desc: 'Ongoing support, updates, and improvements to keep your digital products running.'
              },

            ];

            return (
              <>
                {/* Desktop Grid */}
                <div className="hidden md:grid md:grid-cols-3 gap-5">

                  {items.map(
                    ({ icon, title, desc }, i) => (
                      <div
                        key={title}
                        className="reveal card-black p-7"
                        style={{
                          transitionDelay: `${i * 60}ms`
                        }}
                      >

                        <span className="text-3xl mb-4 block text-white">
                          {icon}
                        </span>

                        <h3
                          className="text-white mb-2"
                          style={{
                            fontFamily: 'Anton,sans-serif',
                            fontWeight: 500
                          }}
                        >
                          {title}
                        </h3>

                        <p className="text-sm text-white/40 leading-relaxed">
                          {desc}
                        </p>

                      </div>
                    )
                  )}

                </div>

                {/* Mobile Swipe Carousel */}
                <div className="md:hidden">

                  <div
                    className="flex gap-4 overflow-x-auto scrollbar-none snap-x snap-mandatory pb-4"
                    style={{ scrollbarWidth: 'none' }}
                  >

                    {items.map(
                      ({ icon, title, desc }) => (
                        <div
                          key={title}
                          className="flex-shrink-0 w-64 card-black p-7 snap-start"
                        >

                          <span className="text-3xl mb-4 block text-white">
                            {icon}
                          </span>

                          <h3
                            className="text-white mb-2"
                            style={{
                              fontFamily: 'Anton,sans-serif',
                              fontWeight: 500
                            }}
                          >
                            {title}
                          </h3>

                          <p className="text-sm text-white/40 leading-relaxed">
                            {desc}
                          </p>

                        </div>
                      )
                    )}

                  </div>

                  <p className="text-center text-xs text-white/30 mt-3">
                    ← Swipe to see more →
                  </p>

                </div>
              </>
            );

          })()}

        </div>
      </section>

      {/* Values */}
      <section className="bg-grid-white py-20 md:py-28">
        <div className="container-max">

          <div className="reveal text-center mb-14">

            <span className="section-label">
              Our Values
            </span>

            <h2 className="section-title">
              HOW WE OPERATE.
            </h2>

          </div>

          <div className="grid sm:grid-cols-2 gap-6 max-w-3xl mx-auto">

            {VALUES.map(
              ({ icon, title, desc }, i) => (
                <div
                  key={title}
                  className="reveal card-white p-7 flex items-start gap-5"
                  style={{
                    transitionDelay: `${i * 60}ms`
                  }}
                >

                  <span className="text-3xl flex-shrink-0">
                    {icon}
                  </span>

                  <div>

                    <h3
                      className="font-medium text-black dark:text-white mb-2"
                      style={{
                        fontFamily: 'Anton,sans-serif',
                        fontWeight: 500
                      }}
                    >
                      {title}
                    </h3>

                    <p className="text-sm text-black/50 dark:text-white/50 leading-relaxed">
                      {desc}
                    </p>

                  </div>

                </div>
              )
            )}

          </div>
        </div>
      </section>

      {/* Team */}
      <section className="bg-grid-black py-20 md:py-28">
        <div className="container-max">

          <div className="reveal text-center mb-14">

            <span className="section-label-inv">
              The Team
            </span>

            <h2 className="section-title-inv">
              THE PEOPLE DOING THE WORK.
            </h2>

            <p className="section-sub-inv mx-auto">
              No juniors handling your project after the sales call.
              The people you meet are the people who build.
            </p>

          </div>

          {/* Desktop Grid */}
          <div className="hidden md:grid md:grid-cols-3 gap-6">

            {TEAM.map(
              ({ name, role, bio, initials }, i) => (
                <div
                  key={name}
                  className="reveal card-black p-7"
                  style={{
                    transitionDelay: `${i * 60}ms`
                  }}
                >

                  <div
                    className="w-14 h-14 rounded-2xl bg-white dark:bg-[#111] flex items-center justify-center text-black dark:text-white font-black text-lg mb-5"
                    style={{
                      fontFamily: 'Anton,sans-serif',
                      fontWeight: 500
                    }}
                  >
                    {initials}
                  </div>

                  <h3
                    className="text-white mb-0.5"
                    style={{
                      fontFamily: 'Anton,sans-serif',
                      fontWeight: 500
                    }}
                  >
                    {name}
                  </h3>

                  <p className="text-xs text-white/40 font-medium mb-3">
                    {role}
                  </p>

                  <p className="text-sm text-white/40 leading-relaxed">
                    {bio}
                  </p>

                </div>
              )
            )}

          </div>

          {/* Mobile Swipe Carousel */}
          <div className="md:hidden">

            <div
              className="flex gap-4 overflow-x-auto scrollbar-none snap-x snap-mandatory pb-4"
              style={{ scrollbarWidth: 'none' }}
            >

              {TEAM.map(
                ({ name, role, bio, initials }) => (
                  <div
                    key={name}
                    className="flex-shrink-0 w-72 card-black p-7 snap-start"
                  >

                    <div
                      className="w-14 h-14 rounded-2xl bg-white dark:bg-[#111] flex items-center justify-center text-black dark:text-white font-black text-lg mb-5"
                      style={{
                        fontFamily: 'Anton,sans-serif',
                        fontWeight: 500
                      }}
                    >
                      {initials}
                    </div>

                    <h3
                      className="text-white mb-0.5"
                      style={{
                        fontFamily: 'Anton,sans-serif',
                        fontWeight: 500
                      }}
                    >
                      {name}
                    </h3>

                    <p className="text-xs text-white/40 font-medium mb-3">
                      {role}
                    </p>

                    <p className="text-sm text-white/40 leading-relaxed">
                      {bio}
                    </p>

                  </div>
                )
              )}

            </div>

            <p className="text-center text-xs text-white/30 mt-3">
              ← Swipe to see more →
            </p>

          </div>

        </div>
      </section>

      {/* Work Images with Lightbox */}
      <section className="bg-grid-white py-20 md:py-28">
        <div className="container-max">

          <div className="reveal text-center mb-14">

            <span className="section-label">
              Our Work
            </span>

            <h2 className="section-title">
              SOME OF OUR PROJECTS.
            </h2>

          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">

            {PROJECTS.map(
              ({ url, alt }, i) => (
                <div
                  key={i}
                  className="reveal overflow-hidden rounded-2xl border border-black/10 dark:border-white/10 group cursor-pointer relative"
                  style={{
                    transitionDelay: `${i * 60}ms`
                  }}
                  onClick={() =>
                    setLightbox({
                      open: true,
                      index: i
                    })
                  }
                >

                  <img
                    src={url}
                    alt={alt}
                    className="img-reveal w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />

                  <div className="absolute inset-0 bg-black/0 dark:bg-white/0 group-hover:bg-black/40 dark:group-hover:bg-white/40 transition-all duration-300 flex items-center justify-center">

                    <svg
                      className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      width="40"
                      height="40"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>

                  </div>

                </div>
              )
            )}

          </div>

        </div>
      </section>

      {/* Lightbox */}
      {lightbox.open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{
            background: 'rgba(0,0,0,0.95)'
          }}
          onClick={close}
        >

          {/* Close */}
          <button
            onClick={close}
            className="absolute top-5 right-5 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors z-10"
            aria-label="Close"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
            >
              <path
                d="M4 4l10 10M14 4L4 14"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </button>

          {/* Counter */}
          <div className="absolute top-5 left-1/2 -translate-x-1/2 text-white/50 text-sm font-medium">
            {lightbox.index + 1} / {PROJECTS.length}
          </div>

          {/* Prev */}
          <button
            onClick={e => {
              e.stopPropagation();
              prev();
            }}
            className="absolute left-4 md:left-8 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors z-10"
            aria-label="Previous"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 16 16"
              fill="none"
            >
              <path
                d="M10 4L6 8l4 4"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          {/* Image */}
          <div
            className="max-w-4xl max-h-[80vh] mx-16 md:mx-24"
            onClick={e => e.stopPropagation()}
          >

            <img
              src={PROJECTS[lightbox.index].url}
              alt={PROJECTS[lightbox.index].alt}
              className="max-w-full max-h-[80vh] object-contain rounded-2xl shadow-2xl"
            />

            <p className="text-center text-white/50 text-sm mt-4">
              {PROJECTS[lightbox.index].alt}
            </p>

          </div>

          {/* Next */}
          <button
            onClick={e => {
              e.stopPropagation();
              next();
            }}
            className="absolute right-4 md:right-8 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors z-10"
            aria-label="Next"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 16 16"
              fill="none"
            >
              <path
                d="M6 4l4 4-4 4"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          {/* Dots */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">

            {PROJECTS.map((_, i) => (
              <button
                key={i}
                onClick={e => {
                  e.stopPropagation();
                  setLightbox(l => ({
                    ...l,
                    index: i
                  }));
                }}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === lightbox.index
                    ? 'bg-white dark:bg-[#111] w-6'
                    : 'bg-white/30 w-3'
                }`}
                aria-label={`Go to ${i + 1}`}
              />
            ))}

          </div>

        </div>
      )}

      {/* CTA */}
      <section className="mb-12 border-t border-white/10">

        <div className="container-max text-center reveal">

          <div className="text-center p-8 bg-black rounded-3xl text-white">

            <h2 className="text-3xl font-medium font-display mb-2 !text-white">
              READY TO WORK TOGETHER?
            </h2>

            <p className="text-white/70 mb-6">
              We are currently accepting new clients. Let's talk about what
              we can build for you.
            </p>

            <Link
              to="/contact"
              className="btn-primary-inv"
            >
              Get Free Quote

              <svg
                width="16"
                height="16"
                viewBox="0 0 18 18"
                fill="none"
                className="ml-2 inline"
              >
                <path
                  d="M3 9H15M11 5l4 4-4 4"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>

            </Link>

          </div>

        </div>

      </section>
    </>
  );
}